'use client';

import { styled } from 'styled-components';
import { ImageProps, SharedHeroProps, VideoProps } from '@types';
import cs from 'classnames';
import * as Styled from '@lib/styles';
import Button from '@dvd/components/Button';
import TextLink from '@dvd/components/TextLink';
import {
  HeroDescription,
  HeroLogos,
  HeroComputedMedia,
  HeroMessaging,
  HeroSubtitle,
  HeroMedia,
} from '@dvd/modules/Heroes/HeroSharedComponents';

interface HeroSplitProps extends SharedHeroProps {
  /**
   * The video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  video?: VideoProps;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   * - Provide the asset’s width and height to reserve its aspect ratio before loading.
   */
  image?: Pick<ImageProps, 'src' | 'alt'> &
    Partial<Pick<ImageProps, 'width' | 'height'>>;
  /**
   * Layout variant of the hero section. Portrait is typically used for videos and images with a 1:1 aspect ratio. Landscape is typically used for videos and images with a 16:9 aspect ratio..
   */
  variant?: 'portrait' | 'landscape';
  /**
   * Whether to show customer logos
   */
  showLogos?: boolean;
  /**
   * Optional heading text displayed above customer logos.
   */
  logosHeading?: string;
  /**
   * Text to display below the primary CTA button
   * - Only used if primaryCta is provided and no secondary CTA is provided
   */
  primaryCtaSubtext?: string;
}

const HeroContainer = styled.div`
  padding-block-end: ${({ theme }) => theme.spacing[3]};
  padding-block-start: ${({ theme }) => theme.spacing[4]};
  display: grid;
  ${({ theme }) => theme.grid.columns.small}

  &.portrait {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  &.landscape {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block-end: ${({ theme }) => theme.spacing[4]};

    &.portrait {
      align-items: center;
      display: grid;
      padding-block-start: ${({ theme }) => theme.spacing[4]};
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 6;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 8 / span 5;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }

    &.landscape {
      align-items: center;
      display: grid;
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 5;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 6 / span 7;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }
  }
`;

const HeroCtaContainer = styled.div<{ $hasSubtext: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $hasSubtext, theme }) =>
    $hasSubtext ? theme.spacing[0.5] : theme.spacing[1.5]};
  margin: ${({ theme }) => theme.spacing[1.5]} auto 0;

  ${({ theme }) => theme.mq.minWidth.medium} {
    flex-direction: ${({ $hasSubtext }) => ($hasSubtext ? 'column' : 'row')};
    flex-wrap: wrap;
    justify-content: center;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: ${({ theme }) => theme.spacing[1.5]} 0 0;
    align-items: flex-start;
    justify-content: flex-start;
  }

  // Remove the extra padding we get from the p tag
  p {
    margin-block-end: 0;
  }
`;

/**
 * This Hero displays a split layout for the Hero. The default variant is landscape.
 *
 * Note: `primaryCtaSubtext` is intentionally suppressed when a `secondaryCta` is present.
 */
const HeroSplit = ({
  description,
  video = null,
  headline,
  image = null,
  primaryCta = null,
  secondaryCta = null,
  linkCta = null,
  showLogos = true,
  logosHeading,
  title,
  variant = 'landscape',
  primaryCtaSubtext = null,
}: HeroSplitProps) => {
  const hasCtaSubtext = !!primaryCtaSubtext && !secondaryCta;
  return (
    <HeroContainer className={cs(variant)}>
      <HeroMessaging>
        <header>
          <Styled.Eyebrow as="h1">{title}</Styled.Eyebrow>
          <HeroSubtitle aria-roledescription="subtitle">
            {headline}
          </HeroSubtitle>
        </header>
        <HeroDescription description={description} size="bodyMd" />

        {primaryCta || secondaryCta || linkCta ? (
          <HeroCtaContainer $hasSubtext={hasCtaSubtext}>
            {primaryCta?.href ? (
              <>
                <Button
                  color={primaryCta.buttonColor ?? 'blue500'}
                  href={primaryCta.href}
                  trackingProperty={primaryCta.trackingProperty}
                  trackingLabel={primaryCta.trackingLabel}
                >
                  {primaryCta.text}
                </Button>
                {/* Only show the subtext if there is no secondary CTA */}
                {hasCtaSubtext ? (
                  <Styled.BodySm>{primaryCtaSubtext}</Styled.BodySm>
                ) : null}
              </>
            ) : null}
            {secondaryCta?.href ? (
              <Button
                variant="secondary"
                href={secondaryCta.href}
                trackingProperty={secondaryCta.trackingProperty}
                trackingLabel={secondaryCta.trackingLabel}
              >
                {secondaryCta.text}
              </Button>
            ) : null}
          </HeroCtaContainer>
        ) : null}
        {/* Needs a separate CtaContainer for the linkCta so it can sit outside of the CtaContainer for the primary and secondary ctas */}
        {linkCta ? (
          <HeroCtaContainer $hasSubtext={false}>
            <Styled.BodyMd>
              <TextLink cta={linkCta} />
            </Styled.BodyMd>
          </HeroCtaContainer>
        ) : null}
      </HeroMessaging>

      <HeroComputedMedia
        hashedId={video?.hashedId}
        imageAlt={image?.alt}
        imageSrc={image?.src}
        imageWidth={image?.width}
        imageHeight={image?.height}
        variant={variant}
        aspect={video?.aspect}
      />
      {showLogos ? <HeroLogos heading={logosHeading} /> : null}
    </HeroContainer>
  );
};

export default HeroSplit;
